import { NectarPageBlock } from './nectarPageBlock.generated';

/**
 * Canonical in-product page blocks/surfaces stamped onto Nectar `click` and
 * `impression` events via the `data-nectar-page-block` DOM attribute / the
 * `page_block` dimension. Reference the `NectarPageBlock` members in code rather
 * than raw strings.
 *
 * `NectarPageBlock` is generated from the backend source of truth
 * `Nectar::PageBlocks::ALL` (app/services/nectar/page_blocks.rb); CI fails if the
 * generated file is stale. To add/rename a value, edit the Ruby and run
 * bin/generate-nectar-page-blocks. Everything below is the hand-authored wrapper.
 */
export { NectarPageBlock };

/** Runtime list of every page block value (membership checks + parity). */
export const NECTAR_PAGE_BLOCKS = Object.values(NectarPageBlock);

/** Empty string is the sentinel for "no page block" (generic clicks). */
export type NectarPageBlockValue = NectarPageBlock | '';

const DATA_NECTAR_PAGE_BLOCK_ATTR = 'data-nectar-page-block';
const NECTAR_PAGE_BLOCK_SELECTOR = `[${DATA_NECTAR_PAGE_BLOCK_ATTR}]`;

const isNectarPageBlock = (value: string | null | undefined): value is NectarPageBlock =>
  value != null && (NECTAR_PAGE_BLOCKS as readonly string[]).includes(value);

/** Narrow an arbitrary string (e.g. a URL search param) to a known page block. */
export const toNectarPageBlock = (value: string | null | undefined): NectarPageBlock | undefined =>
  isNectarPageBlock(value) ? value : undefined;

/**
 * Resolve the page block for an event target by walking up to the nearest
 * `[data-nectar-page-block]` ancestor (or the target itself). `closest` is what lets
 * clicks on child nodes (button text, icons) still resolve the CTA's page block.
 * Unknown/absent values collapse to '' so only canonical page blocks reach analytics.
 */
export const resolveNectarPageBlock = (target: Element | null): NectarPageBlockValue => {
  const value = target?.closest(NECTAR_PAGE_BLOCK_SELECTOR)?.getAttribute(DATA_NECTAR_PAGE_BLOCK_ATTR);
  return isNectarPageBlock(value) ? value : '';
};

/**
 * Strongly-typed `data-nectar-page-block` attribute for authoring CTAs. Spread onto
 * any element/primitive that forwards DOM props:
 * `<PillButton {...nectarPageBlock(NectarPageBlock.CommunityJoinBanner)} />`.
 * A typo or unlisted value is a compile error.
 */
export const nectarPageBlock = (pageBlock: NectarPageBlock): { [DATA_NECTAR_PAGE_BLOCK_ATTR]: NectarPageBlock } => ({
  [DATA_NECTAR_PAGE_BLOCK_ATTR]: pageBlock,
});
